import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import api from '../../services/api';

function Apps() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  
  // Formularios
  const [newApp, setNewApp] = useState({
    bundleId: '',
    subdomain: '',
    parseId: '',
    ios: 100,
    android: 100
  });
  
  const [versionUpdate, setVersionUpdate] = useState({
    ios: '',
    android: ''
  });
  
  const [editingApp, setEditingApp] = useState(null);
  const [duplicateData, setDuplicateData] = useState({
    sourceBundleId: '',
    newBundleId: ''
  });

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bundles');
      setApps(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Error cargando aplicaciones: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    try {
      await api.post('/bundles', newApp);
      setSuccess('Aplicación creada exitosamente');
      setShowCreateModal(false);
      setNewApp({ bundleId: '', subdomain: '', parseId: '', ios: 100, android: 100 });
      loadApps();
    } catch (err) {
      setError('Error creando aplicación: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdateVersions = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (versionUpdate.ios) updateData.ios = parseInt(versionUpdate.ios);
      if (versionUpdate.android) updateData.android = parseInt(versionUpdate.android);
      
      const response = await api.post('/bundles/update-versions', updateData);
      setSuccess(response.data.message);
      setShowVersionModal(false);
      setVersionUpdate({ ios: '', android: '' });
      loadApps();
    } catch (err) {
      setError('Error actualizando versiones: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditApp = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/bundles/${editingApp.id}`, editingApp);
      setSuccess('Aplicación actualizada exitosamente');
      setShowEditModal(false);
      setEditingApp(null);
      loadApps();
    } catch (err) {
      setError('Error actualizando aplicación: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteApp = async (bundleId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta aplicación?')) return;
    
    try {
      await api.delete(`/bundles/${bundleId}`);
      setSuccess('Aplicación eliminada exitosamente');
      loadApps();
    } catch (err) {
      setError('Error eliminando aplicación: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDuplicateApp = async (e) => {
    e.preventDefault();
    try {
      await api.post('/bundles/duplicate', duplicateData);
      setSuccess('Aplicación duplicada exitosamente');
      setShowDuplicateModal(false);
      setDuplicateData({ sourceBundleId: '', newBundleId: '' });
      loadApps();
    } catch (err) {
      setError('Error duplicando aplicación: ' + (err.response?.data?.message || err.message));
    }
  };

  const openEditModal = (app) => {
    setEditingApp({ ...app });
    setShowEditModal(true);
  };

  const openDuplicateModal = (app) => {
    setDuplicateData({ sourceBundleId: app.id, newBundleId: '' });
    setShowDuplicateModal(true);
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Gestión de Aplicaciones</h2>
          <p className="text-muted">Administra las aplicaciones móviles y sus configuraciones</p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Aplicaciones Registradas</h5>
              <div>
                <Button 
                  variant="outline-primary" 
                  className="me-2"
                  onClick={() => setShowVersionModal(true)}
                >
                  Actualizar Versiones
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Nueva Aplicación
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Cargando aplicaciones...</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Bundle ID</th>
                      <th>APIs</th>
                      <th>Parse IDs</th>
                      <th>Versiones</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map(app => (
                      <tr key={app.id}>
                        <td>
                          <code>{app.id}</code>
                        </td>
                        <td>
                          <div>
                            <strong>Prod:</strong> <a href={app.productionApi} target="_blank" rel="noopener noreferrer">{app.productionApi}</a><br/>
                            <strong>Sandbox:</strong> <a href={app.sandboxApi} target="_blank" rel="noopener noreferrer">{app.sandboxApi}</a>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>Prod:</strong> <code>{app.productionIdParse}</code><br/>
                            <strong>Sandbox:</strong> <code>{app.sandboxIdParse}</code>
                          </div>
                        </td>
                        <td>
                          <Badge bg="info" className="me-1">iOS: {app.ios}</Badge>
                          <Badge bg="success">Android: {app.android}</Badge>
                        </td>
                        <td>
                          <Button 
                            size="sm" 
                            variant="outline-primary" 
                            className="me-1"
                            onClick={() => openEditModal(app)}
                          >
                            Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline-secondary" 
                            className="me-1"
                            onClick={() => openDuplicateModal(app)}
                          >
                            Duplicar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline-danger"
                            onClick={() => handleDeleteApp(app.id)}
                          >
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal Crear Aplicación */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Nueva Aplicación</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateApp}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Bundle ID *</Form.Label>
              <Form.Control
                type="text"
                placeholder="com.dilernen.ejemplo"
                value={newApp.bundleId}
                onChange={(e) => setNewApp({...newApp, bundleId: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Subdominio *</Form.Label>
              <Form.Control
                type="text"
                placeholder="ejemplo"
                value={newApp.subdomain}
                onChange={(e) => setNewApp({...newApp, subdomain: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                Se creará: https://{newApp.subdomain || 'ejemplo'}.dilernen.com
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Parse ID *</Form.Label>
              <Form.Control
                type="text"
                placeholder="ejemplo-leon"
                value={newApp.parseId}
                onChange={(e) => setNewApp({...newApp, parseId: e.target.value})}
                required
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Versión iOS</Form.Label>
                  <Form.Control
                    type="number"
                    value={newApp.ios}
                    onChange={(e) => setNewApp({...newApp, ios: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Versión Android</Form.Label>
                  <Form.Control
                    type="number"
                    value={newApp.android}
                    onChange={(e) => setNewApp({...newApp, android: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Crear Aplicación
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Actualizar Versiones */}
      <Modal show={showVersionModal} onHide={() => setShowVersionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Actualizar Versiones en Lote</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateVersions}>
          <Modal.Body>
            <Alert variant="info">
              Actualiza las versiones de todas las aplicaciones de una vez. 
              Deja en blanco las versiones que no quieras cambiar.
            </Alert>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Nueva Versión iOS</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Ej: 810"
                    value={versionUpdate.ios}
                    onChange={(e) => setVersionUpdate({...versionUpdate, ios: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Nueva Versión Android</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Ej: 810"
                    value={versionUpdate.android}
                    onChange={(e) => setVersionUpdate({...versionUpdate, android: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowVersionModal(false)}>
              Cancelar
            </Button>
            <Button variant="warning" type="submit">
              Actualizar Todas
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Editar Aplicación */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Aplicación</Modal.Title>
        </Modal.Header>
        {editingApp && (
          <Form onSubmit={handleEditApp}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Bundle ID</Form.Label>
                <Form.Control
                  type="text"
                  value={editingApp.id}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>API Producción</Form.Label>
                <Form.Control
                  type="text"
                  value={editingApp.productionApi || ''}
                  onChange={(e) => setEditingApp({...editingApp, productionApi: e.target.value})}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Parse Producción</Form.Label>
                <Form.Control
                  type="text"
                  value={editingApp.productionParse || ''}
                  onChange={(e) => setEditingApp({...editingApp, productionParse: e.target.value})}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Parse ID Producción</Form.Label>
                <Form.Control
                  type="text"
                  value={editingApp.productionIdParse || ''}
                  onChange={(e) => setEditingApp({...editingApp, productionIdParse: e.target.value})}
                />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Versión iOS</Form.Label>
                    <Form.Control
                      type="number"
                      value={editingApp.ios || ''}
                      onChange={(e) => setEditingApp({...editingApp, ios: parseInt(e.target.value) || 0})}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Versión Android</Form.Label>
                    <Form.Control
                      type="number"
                      value={editingApp.android || ''}
                      onChange={(e) => setEditingApp({...editingApp, android: parseInt(e.target.value) || 0})}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit">
                Guardar Cambios
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Modal Duplicar Aplicación */}
      <Modal show={showDuplicateModal} onHide={() => setShowDuplicateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Duplicar Aplicación</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleDuplicateApp}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Aplicación Origen</Form.Label>
              <Form.Control
                type="text"
                value={duplicateData.sourceBundleId}
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nuevo Bundle ID *</Form.Label>
              <Form.Control
                type="text"
                placeholder="com.dilernen.nuevaapp"
                value={duplicateData.newBundleId}
                onChange={(e) => setDuplicateData({...duplicateData, newBundleId: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                Se creará una copia exacta con el nuevo Bundle ID
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDuplicateModal(false)}>
              Cancelar
            </Button>
            <Button variant="success" type="submit">
              Duplicar Aplicación
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Apps;